import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Tab,
  TabList,
  Text,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Delete20Regular } from '@fluentui/react-icons';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { useCurrentApp } from '@/hooks/useCurrentApp';
import { useAppWorkflows } from '@/hooks/useAppResource';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useIsProjectOwner } from '@/hooks/useUserPermissions';
import { ResourceErrorPage } from '@/components/shared/ResourceErrorPage';
import { AppOverviewHeader } from '@/components/shared/AppOverviewHeader';
import { PageSection } from '@/components/shared/PageSection';
import { ApiError } from '@/services/apiError';
import { useAuth } from '@/auth';
import { deleteAutomationApp } from '@/services/projectService';
import { useAppStore } from '@/stores/appStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { DeleteConfirmDialog } from '@/components/common/DeleteConfirmDialog';
import { useApplicationConfigTabs } from '@/components/SettingsDialog/useApplicationConfigTabs';
import { useAppPermission } from '@/hooks/useAppPermission';
import { customScrollbarStyles } from '@/styles/scrollbar';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  tabsContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  tabList: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
  },
  content: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    overflow: 'auto',
    ...customScrollbarStyles,
  },
  dangerZone: {
    marginTop: tokens.spacingVerticalXXXL,
    paddingTop: tokens.spacingVerticalL,
    borderTop: `1px solid ${tokens.colorPaletteRedBorder2}`,
  },
  dangerTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorPaletteRedForeground1,
    marginBottom: tokens.spacingVerticalS,
  },
  dangerDescription: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalM,
  },
  deleteButton: {
    backgroundColor: tokens.colorPaletteRedBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
    '@media (forced-colors: active)': {
      backgroundColor: 'ButtonFace',
      color: 'ButtonText',
    },
  },
});

export const AppSettingsPage = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const selectedLocation = useAppStore((state => state.selectedLocation));
  const { projectName, locationName } = useCurrentProject();
  const { app, appName, isLoading, notFound } = useCurrentApp();
  const { data: workflows } = useAppWorkflows(app ?? null);
  useDocumentTitle(['Settings', appName, projectName]);
  const { getOttoToken } = useAuth();
  const { canDelete } = useAppPermission();
  const { data: isProjectAdmin } = useIsProjectOwner(projectName ?? '');
  const userCanDelete = useMemo(() => canDelete || isProjectAdmin, [canDelete, isProjectAdmin]);

  const [selectedTab, setSelectedTab] = useState<string>('general');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteConfirm = useCallback(async () => {
    if (!app || !projectName) return;
    setIsDeleting(true);
    try {
      const success = await deleteAutomationApp(projectName, app.name, getOttoToken);
      if (success) {
        useAppStore.getState().clearSelectedApp();
        const loc = locationName ?? selectedLocation;
        const locPrefix = loc ? `/location/${encodeURIComponent(loc)}` : '';
        const tenantId = useAppStore.getState().selectedTenantId;
        const tp = tenantId ? `/tenant/${encodeURIComponent(tenantId)}` : '';
        navigate(`${tp}${locPrefix}/projects/${encodeURIComponent(projectName)}/apps`);
      } else {
        useNotificationStore.getState().addNotification({
          type: 'error',
          title: 'Delete failed',
          message: `Failed to delete application "${app.name}". Please try again.`,
        });
      }
    } catch (err) {
      useNotificationStore.getState().addNotification({
        type: 'error',
        title: 'Delete failed',
        message: err instanceof Error ? err.message : 'An unexpected error occurred.',
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  }, [app, projectName, locationName, getOttoToken, selectedLocation, navigate]);

  const dangerZone = app ? (
    <div className={styles.dangerZone}>
      <Text className={styles.dangerTitle} block>
        Danger Zone
      </Text>
      <Text className={styles.dangerDescription} block>
        Permanently delete this application and all its workflows. This action cannot be undone.
      </Text>
      <Button
        appearance="primary"
        icon={<Delete20Regular />}
        onClick={() => setDeleteDialogOpen(true)}
        className={styles.deleteButton}
      >
        Delete application
      </Button>
    </div>
  ) : undefined;

  const { tabs } = useApplicationConfigTabs({
    projectName: projectName ?? '',
    appName: appName ?? '',
    permissionsExtraContent: userCanDelete ? dangerZone : undefined,
  });

  const currentTab = tabs.find((t) => t.value === selectedTab) ?? tabs[0];

  if (isLoading) {
    return <Spinner size="medium" label="Loading app settings..." />;
  }

  if (notFound) {
    return <ResourceErrorPage resourceType="application" error={new ApiError('Not found', 404)} />;
  }

  return (
    <div className={styles.root}>
      {appName && (
        <AppOverviewHeader
          name={appName}
          app={app}
          region={locationName}
          workflowCount={workflows?.length ?? 0}
        />
      )}
      <PageSection title="Settings">
        <div className={styles.tabsContainer}>
          <TabList
            className={styles.tabList}
            selectedValue={selectedTab}
            onTabSelect={(_, data) => setSelectedTab(data.value as string)}
            aria-label="Application settings"
          >
            {tabs.map((tab) => (
              <Tab key={tab.value} value={tab.value} icon={tab.icon ?? null}>
                {tab.label}
              </Tab>
            ))}
          </TabList>
          <div className={styles.content}>{currentTab?.content(null)}</div>
        </div>
      </PageSection>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemType="application"
        itemNames={[appName ?? '']}
        isDeleting={isDeleting}
      />
    </div>
  );
};
